import { CONVERSION_KPI_OPTION, CTR_KPI_OPTION, VCR_KPI_OPTION } from '../constants/lineItems';
import { LineItem } from '../validationSchemas/LineItemSchema';
import { addCommas, formatServerDate, round } from './helpers';
import { camelCaseKeys } from './object';

const parseKpiGoal = (kpiGoal: string, defaultSecondaryKpi: object) => {
  let returnValue: any = { secondaryKpi: [defaultSecondaryKpi] };
  try {
    returnValue = (kpiGoal && JSON.parse(kpiGoal)) || returnValue;
    // Convert thel KPI goal values to a string, as there may be a mix of numbers and strings
    Object.keys(returnValue).forEach((kpiType) => {
      returnValue[kpiType] = returnValue[kpiType].map((item) => {
        const newItem = { ...item };
        Object.entries(newItem).forEach(([name, value]) => {
          newItem[name] = value?.toString();
        });
        return newItem;
      });
    });
  } catch {
    // Legacy kpi_goal may be in the format "CTR:0.5;"
    returnValue = {
      secondaryKpi: kpiGoal
        .split(';')
        .filter((kpi) => kpi)
        .map((kpi) => {
          const [name, value] = kpi.split(':');
          return { [name || '']: value?.toString() };
        }),
    };
  }
  return returnValue;
};

const normalizeKpiObjectives = (lineItem, kpiGoal: object) => {
  const returnValue = [];
  ['secondaryKpi', 'coreKpi'].forEach((kpiType) =>
    kpiGoal[kpiType]?.forEach((item) => {
      const [name, value] = Object.entries(item)[0];
      returnValue.push({
        name,
        // Conversion is scored-list-only with no goal, so pin it to the sentinel value
        // regardless of what was stored, keeping kpiGoal/scoredListMetric consistent.
        value: name === CONVERSION_KPI_OPTION.value ? CONVERSION_KPI_OPTION.defaultValue : value,
        type: kpiType,
        adGroupIds: (lineItem?.seed_type_ids && lineItem.seed_type_ids[name]) || [],
      });
    })
  );
  return returnValue;
};

const mapLineItemToForm = (lineItem: any): LineItem => {
  const mediaType = lineItem?.media_type || 'display';
  const defaultKpiOption = mediaType === 'video' ? VCR_KPI_OPTION : CTR_KPI_OPTION;

  const defaultSecondaryKpi = {
    [defaultKpiOption.value]: defaultKpiOption.defaultValue,
  };
  const kpiGoal = parseKpiGoal(lineItem?.kpi_goal, defaultSecondaryKpi);
  const kpiObjectives = normalizeKpiObjectives(lineItem, kpiGoal);
  const scoredListMetric = lineItem?.scored_list_metric || defaultKpiOption.value;

  return {
    ...camelCaseKeys(lineItem),
    // General
    lineItemId: lineItem?.id,
    lineItemName: lineItem?.name || '',
    insertionOrderNumber: lineItem?.insertion_order_number ?? null,
    // lineItemNotes: lineItemData?.notes || '', // not yet supported
    contractId: lineItem?.contract_id,
    mediaType,
    // Override the default to be 'any' as server will return 'geofencing' as default
    // and we do not expose it in the UI for user to change it to 'any'.
    targetStrategy: 'any',
    thinklabState: lineItem?.thinklab_state || 'approved',

    // Timing
    startDate: lineItem?.start_date ? formatServerDate(lineItem.start_date) : null,
    endDate: lineItem?.final_end_date ? formatServerDate(lineItem.final_end_date) : null,
    frequencyCapEnabled: !!lineItem?.frequency_capping,
    frequencyCapping: lineItem?.frequency_capping || 'daily',
    frequencyCappingScope: lineItem?.frequency_capping_scope || 'device',
    maxUserViewsPerPeriod: lineItem?.max_user_views_per_period || '',
    performanceCapEnabled: !!lineItem?.click_frequency_capping,
    clickFrequencyCapping: lineItem?.click_frequency_capping || 'daily',
    clickFrequencyCappingScope: lineItem?.click_frequency_capping_scope || 'device',
    maxUserClicksPerPeriod: lineItem?.max_user_clicks_per_period || '',

    // Reporting
    // pull defaults from cost_incurring_features array
    costIncurringFeatures: lineItem?.cost_incurring_features || [],

    // Budget & Goals
    advisoryBudget: lineItem?.advisory_budget ? addCommas(lineItem.advisory_budget) : '0',
    impressionBudget: lineItem?.impression_budget ? lineItem?.impression_budget.toString() : '0', // which of these is represented in the UX mock?
    totalImpressions: lineItem?.total_impressions ? lineItem?.total_impressions.toString() : '0',
    bonus: lineItem?.bonus ? addCommas(lineItem.bonus) : '0',
    pricingRate: lineItem?.pricing_rate ? (lineItem.pricing_rate / 100).toFixed(2).toString() : '0',
    optimizationType: lineItem?.optimization_type || 'CTR V2',
    minCtrOptimization: lineItem?.min_ctr_optimization || '',
    minConversionOptimization: lineItem?.min_conversion_optimization || '',
    bidPriority: lineItem?.bid_priority || 40,

    // Extended settings
    bidFrequency: lineItem?.bid_frequency || 1,
    pacingStrategies: lineItem?.pacing_strategies || [],
    dailyImpressionLimit: lineItem?.daily_impression_limit || 0,
    dollarBudget: (lineItem?.dollar_budget && round(lineItem?.dollar_budget)) || 0,
    uiState: lineItem?.ui_state || 'incomplete',
    deliveryStatus: lineItem?.delivery_status || '',
    pidType: lineItem?.pid_type,
    kpiGoal,
    // Set seedTypeIds so that the seedTypeIds keys won't be camelcased
    seedTypeIds: lineItem?.seed_type_ids,
    kpiObjectives,
    scoredListMetric,
    visitStoreList: lineItem?.visit_store_list && {
      ...camelCaseKeys(lineItem.visit_store_list),
      storeList: lineItem.visit_store_list.store_list ? lineItem.visit_store_list.store_list.split(',') : [],
    },
    receiptProductList: lineItem?.receipt_product_list && {
      ...camelCaseKeys(lineItem.receipt_product_list),
      productList: lineItem.receipt_product_list.product_list
        ? lineItem.receipt_product_list.product_list.split(',')
        : [],
      productLowerCategories: lineItem.receipt_product_list.product_lower_categories
        ? lineItem.receipt_product_list.product_lower_categories.split(',')
        : [],
    },
    customDeliveryProfiles: (lineItem?.delivery_profiles || []).map((p: any) => {
      const isActive = p.is_active ?? p.isActive;
      return {
        id: p.id,
        profile_id: p.id,
        io_id: String(p.insertion_order_id ?? p.insertionOrderId ?? lineItem?.id),
        startDate: (p.start_date ?? p.startDate)
          ? formatServerDate((p.start_date ?? p.startDate) as string)
          : null,
        endDate: (p.end_date ?? p.endDate)
          ? formatServerDate((p.end_date ?? p.endDate) as string)
          : null,
        bonus: p.profile_bonus ?? p.profileBonus ?? 0,
        dollarBudget: p.profile_dollar_budget ?? p.profileDollarBudget ?? '0',
        deliveredImpressions: p.delivered_impressions ?? p.deliveredImpressions ?? 0,
        profileImpressionBudget: p.profile_impression_budget ?? 0,
        disabled: p.disabled === true || p.disabled === 1 || p.disabled === 'true',
        active: isActive === true || isActive === 1 || isActive === 'true',
      };
    }),
    deletedProfileIds: [],
  };
};

export default mapLineItemToForm;
